import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/SearchPage.css';

const Search = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    ticker: '',
    period: '1y',
    prediction_days: 30,
    include_news: true,
    risk_tolerance: 50
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Period options
  const periodOptions = [
    { value: '1mo', label: '1 Month' },
    { value: '3mo', label: '3 Months' },
    { value: '6mo', label: '6 Months' },
    { value: '1y', label: '1 Year' },
    { value: '2y', label: '2 Years' },
    { value: '5y', label: '5 Years' }
  ];

  // Risk tolerance labels
  const riskLabels = {
    0: 'Very Conservative',
    25: 'Conservative',
    50: 'Moderate',
    75: 'Aggressive',
    100: 'Very Aggressive'
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'prediction_days' || field === 'risk_tolerance' ? Number(value) : value
    }));
  };

  const handleSliderChange = (event) => {
    const value = Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      risk_tolerance: value
    }));
  };

  const handleSwitchChange = (event) => {
    setFormData(prev => ({
      ...prev,
      include_news: event.target.checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // First, validate the ticker exists
      const validationResponse = await fetch(`/api/stocks/${formData.ticker}/validate`);
      
      if (!validationResponse.ok) {
        throw new Error('Invalid ticker symbol');
      }

      // Navigate to prediction page with search parameters
      const queryParams = new URLSearchParams({
        symbol: formData.ticker,
        period: formData.period,
        prediction_days: formData.prediction_days,
        include_news: formData.include_news,
        risk_tolerance: formData.risk_tolerance
      });

      navigate(`/prediction?${queryParams.toString()}`);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSearch = (ticker) => {
    setFormData(prev => ({
      ...prev,
      ticker: ticker
    }));
  };

  // Popular tickers for quick search
  const popularTickers = [
    { symbol: 'AAPL', name: 'Apple Inc.' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.' },
    { symbol: 'MSFT', name: 'Microsoft Corp.' },
    { symbol: 'TSLA', name: 'Tesla Inc.' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.' },
    { symbol: 'META', name: 'Meta Platforms Inc.' }
  ];

  return (
    <div className="search-container">
      {/* <div className="search-header">
        <h1 className="search-title">Stock Prediction Analysis</h1>
        <p className="search-subtitle">Analyze stock performance and get AI-powered predictions</p>
      </div> */}

      <div className="search-content">
        {/* Search Form */}
        <div className="search-form-container">
          <form className="search-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-alert">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {/* Ticker Input */}
            <div className="form-group">
              <label htmlFor="ticker" className="form-label">
                Stock Ticker Symbol
              </label>
              <div className="input-with-icon">
                <span className="input-icon">🔍</span>
                <input
                  id="ticker"
                  type="text"
                  className="form-input"
                  value={formData.ticker}
                  onChange={handleInputChange('ticker')}
                  placeholder="e.g., AAPL, TSLA, GOOGL"
                  required
                />
              </div>
              <div className="form-helper">
                Enter the stock ticker symbol (e.g., AAPL for Apple)
              </div>
            </div>

            <div className="form-row">
              {/* Period Selection */}
              <div className="form-group">
                <label htmlFor="period" className="form-label">
                  Analysis Period
                </label>
                <select
                  id="period"
                  className="form-select"
                  value={formData.period}
                  onChange={handleInputChange('period')}
                >
                  {periodOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Prediction Days */}
              <div className="form-group">
                <label htmlFor="prediction_days" className="form-label">
                  Prediction Days
                </label>
                <input
                  id="prediction_days"
                  type="number"
                  className="form-input"
                  value={formData.prediction_days}
                  onChange={handleInputChange('prediction_days')}
                  min="1"
                  max="365"
                />
                <div className="form-helper">
                  Number of days to predict into future
                </div>
              </div>
            </div>

            {/* Risk Tolerance Slider */}
            <div className="form-group">
              <label className="form-label">
                Risk Tolerance: <strong>{riskLabels[formData.risk_tolerance]}</strong>
              </label>
              <input
                type="range"
                className="risk-slider"
                value={formData.risk_tolerance}
                onChange={handleSliderChange}
                min="0"
                max="100"
                step="25"
              />
              <div className="slider-labels">
                <span>Very Low</span>
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
                <span>Very High</span>
              </div>
              <div className="risk-description">
                {formData.risk_tolerance <= 25 && 'Focus on stable, established companies with consistent dividends'}
                {formData.risk_tolerance > 25 && formData.risk_tolerance <= 50 && 'Balanced approach with mix of stable and growth stocks'}
                {formData.risk_tolerance > 50 && formData.risk_tolerance <= 75 && 'Growth-oriented with some higher-risk opportunities'}
                {formData.risk_tolerance > 75 && 'Aggressive growth strategy with high-risk, high-reward stocks'}
              </div>
            </div>

            {/* Include News Switch */}
            <div className="form-group">
              <label className="switch-container">
                <input
                  type="checkbox"
                  checked={formData.include_news}
                  onChange={handleSwitchChange}
                  className="switch-input"
                />
                <span className="switch-slider"></span>
                <span className="switch-label">
                  <span className="switch-icon">📰</span>
                  Include News & Sentiment Analysis
                </span>
              </label>
              <div className="form-helper">
                Analyze recent news and market sentiment for better prediction accuracy
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-button"
              disabled={loading || !formData.ticker}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Analyzing...
                </>
              ) : (
                <>
                  <span className="button-icon">📊</span>
                  Generate Prediction
                </>
              )}
            </button>
          </form>
        </div>

        {/* Sidebar */}
        <div className="search-sidebar">
          {/* Popular Stocks */}
          <div className="sidebar-card">
            <h3 className="card-title">
              <span className="card-icon">📈</span>
              Popular Stocks
            </h3>
            <div className="popular-stocks-grid">
              {popularTickers.map(stock => (
                <button
                  key={stock.symbol}
                  className="stock-button"
                  onClick={() => handleQuickSearch(stock.symbol)}
                  type="button"
                >
                  <div className="stock-symbol">{stock.symbol}</div>
                  <div className="stock-name">{stock.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="sidebar-card">
            <h3 className="card-title">How It Works</h3>
            <p>Our prediction model analyzes:</p>
            <ul className="feature-list">
              <li>
                <strong>Historical Price Data:</strong> {periodOptions.find(p => p.value === formData.period)?.label.toLowerCase()} of market performance
              </li>
              <li>
                <strong>Technical Indicators:</strong> Moving averages, RSI, MACD
              </li>
              <li>
                <strong>Market Sentiment:</strong> 
                {formData.include_news ? ' News analysis included' : ' News analysis excluded'}
              </li>
              <li>
                <strong>Risk Profile:</strong> {riskLabels[formData.risk_tolerance].toLowerCase()} strategy
              </li>
            </ul>
          </div>

          {/* Current Settings Preview */}
          <div className="sidebar-card">
            <h3 className="card-title">Analysis Settings</h3>
            <div className="settings-list">
              <div className="setting-item">
                <span className="setting-label">Ticker:</span>
                <span className="setting-value">{formData.ticker || 'Not selected'}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Period:</span>
                <span className="setting-value">{periodOptions.find(p => p.value === formData.period)?.label}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Prediction:</span>
                <span className="setting-value">{formData.prediction_days} days</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">Risk:</span>
                <span className="setting-value">{riskLabels[formData.risk_tolerance]}</span>
              </div>
              <div className="setting-item">
                <span className="setting-label">News:</span>
                <span className="setting-value">{formData.include_news ? 'Included' : 'Excluded'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;